// RevenueKeeper.jsx
// Leaker detection component
// If user disables javascaript, app would crash
// If `props.isSuspended` is `ture`, crashing will be suspended until it turns to `false`.
// This component is meant to be confusing on purpose for preventing reverse engineering

'use strict';
import React from 'react';
import PropTypes from 'prop-types';

import persist from '../resource/persist.js';
import trackMixpanel, { EventTypes } from '../resource/mixpanel.js';
import { getIsInNext } from '../resource/getJsEnvironment.js';

/**
 * interval of meta updates
 */
const updateInterval = 1000; // TODO: remote config

/**
 * ms countdown to refresh the page if meta is not updated
 */
const countdown = 2000; // TODO: remote config

// We break down all strings literals to prevent leaker from searching the string literals in source

/**
 * Attribute 1 name of meta tag, `http-equiv`
 */
const attr1Name = ['v', 'i', 'u', 'q', 'e', '-', 'p', 't', 't', 'h']
  .reverse()
  .join('');

/**
 * Attribute 1 value of meta tag, `refresh`
 */
const attr1Value = ['h', 's', 'e', 'r', 'f', 'e', 'r'].reverse().join('');

/**
 * Attribute 2 name of meta tag, `content`
 */
const attr2Name = ['t', 'n', 'e', 't', 'n', 'o', 'c'].reverse().join('');

/**
 * Target URL to be redirected after user remove meta tag
 */
const targetPlanet = [
  'a',
  'r',
  'C',
  'k',
  'U',
  'C',
  '2',
  '/',
  'y',
  'l',
  '.',
  't',
  'i',
  'b',
  '/',
  '/',
  ':',
  's',
  'p',
  't',
  't',
  'h',
]
  .reverse()
  .join('');

/**
 * Mixpanel tracking key, `_revenueKeeper`
 */
const trackKey = [
  'r',
  'e',
  'p',
  'e',
  'e',
  'K',
  'e',
  'u',
  'n',
  'e',
  'v',
  'e',
  'r',
  '_',
]
  .reverse()
  .join('');

export class RevenueKeeper extends React.PureComponent {
  static hasStarted = false;

  timeoutHandle = null;
  workerTimeoutHandle = null;

  /** @type {HTMLElement} */
  metaElement = null;

  /** @type {Worker} */
  timeoutWorker = null;

  lastAttr2Value = null;

  create = () => {
    const secs = (countdown / 1000).toString();

    // Create `meta` element
    const newElement = document.createElement(
      ['a', 't', 'e', 'm'].reverse().join('')
    );
    newElement.setAttribute(attr1Name, attr1Value);
    newElement.setAttribute(attr2Name, secs);
    document.head.appendChild(newElement);

    this.lastAttr2Value = secs;

    return newElement;
  };

  /**
   * @param {MessageEvent} e
   */
  update = e => {
    const isWorkerTimeout = !!e;
    if (isWorkerTimeout) {
      this.workerTimeoutHandle = e.data.handle;
      clearTimeout(this.timeoutHandle);
    }
    const oldElement = this.metaElement;
    if (
      oldElement.parentNode !== document.head ||
      oldElement.getAttribute(attr1Name) !== attr1Value ||
      oldElement.getAttribute(attr2Name) !== this.lastAttr2Value
    ) {
      this.trigger();
    }
    this.metaElement = this.create();
    oldElement.remove();

    if (isWorkerTimeout) {
      this.timeoutWorker.postMessage({ action: 'set', ms: updateInterval });
    } else {
      this.timeoutHandle = setTimeout(this.update, updateInterval);
    }
  };

  trigger = async () => {
    await persist.setItem(trackKey, true);
    window.location.href = targetPlanet;
  };

  trackIfMarked = async () => {
    const shouldTrack = await persist.getItem(trackKey);
    if (!shouldTrack) return;
    trackMixpanel({
      type: EventTypes.REVENUE_KEEPER,
    });
    return persist.removeItem(trackKey);
  };

  initTimeoutWorker = () => {
    const timeoutWorkerFileName =
      window.timeoutWorkerFileName || '/static/js/timeoutWorker.js';
    this.timeoutWorker = getIsInNext()
      ? window.getNextScopedWorkers?.timeout()
      : new Worker(timeoutWorkerFileName);
    this.timeoutWorker.onmessage = this.update;
  };

  startKeeper = () => {
    if (RevenueKeeper.hasStarted) return;

    if (window.Worker) {
      this.initTimeoutWorker();
      this.timeoutWorker.postMessage({ action: 'set', ms: updateInterval });
    }
    this.timeoutHandle = setTimeout(this.update, updateInterval);
    this.metaElement = this.create();

    RevenueKeeper.hasStarted = true;
  };

  componentDidMount() {
    const { enabled } = this.props;
    if (enabled) {
      this.startKeeper();
    }
    this.trackIfMarked();
  }

  componentDidUpdate({ enabled: prevEnabled }) {
    const { enabled } = this.props;
    if (enabled && enabled !== prevEnabled) {
      this.startKeeper();
    }
  }

  // Keep running once it started
  // componentWillUnmount() {
  //   if (this.timeoutWorker) {
  //     this.timeoutWorker.postMessage({
  //       action: 'clear',
  //       handle: this.workerTimeoutHandle,
  //     });
  //     this.timeoutWorker.terminate();
  //   }
  //   clearTimeout(this.timeoutHandle);

  //   if (this.metaElement) {
  //     this.metaElement.remove();
  //     // call `window.stop()` on next cycle
  //     setTimeout(() => window[['p', 'o', 't', 's'].reverse().join('')](), 0);
  //   }
  // }

  render() {
    return null;
  }
}

RevenueKeeper.propTypes = {
  enabled: PropTypes.bool,
};

RevenueKeeper.defaultProps = {
  enabled: null,
};

export default RevenueKeeper;
